import React, { useState } from 'react';
import { mockPeopleData } from '../../data/mockPeopleData';

interface InitialsAvatarProps {
  personId?: string;
  name?: string; // Person's name
  avatarUrl?: string; // Direct avatar URL
  size?: number;
}

const AVATAR_COLORS = [
  { bg: '#EF4444', text: '#FFFFFF' }, // Red
  { bg: '#F59E0B', text: '#FFFFFF' }, // Orange
  { bg: '#10B981', text: '#FFFFFF' }, // Green
  { bg: '#3B82F6', text: '#FFFFFF' }, // Blue
  { bg: '#8B5CF6', text: '#FFFFFF' }, // Purple
  { bg: '#EC4899', text: '#FFFFFF' }, // Pink
  { bg: '#14B8A6', text: '#FFFFFF' }, // Teal
  { bg: '#F97316', text: '#FFFFFF' }, // Orange
  { bg: '#06B6D4', text: '#FFFFFF' }, // Cyan
  { bg: '#6366F1', text: '#FFFFFF' }, // Indigo
];

/**
 * Extracts initials from a name string
 * Supports both "FirstName LastName" and single names
 * Returns up to 2 characters
 */
const getInitials = (name: string): string => {
  if (!name || typeof name !== 'string') return '';
  
  const trimmedName = name.trim();
  if (!trimmedName) return '';
  
  const parts = trimmedName.split(/\s+/);
  
  if (parts.length === 1) {
    // Single name: take first 2 characters
    return parts[0].substring(0, 2).toUpperCase();
  }
  
  // Multiple names: take first character of first and last parts
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generates a consistent color based on initials
 * Same initials will always get the same color
 */
const getColorForInitials = (initials: string): { bg: string; text: string } => {
  const hash = initials.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const colorIndex = hash % AVATAR_COLORS.length;
  return AVATAR_COLORS[colorIndex];
};

/**
 * Get person data by ID from mock data
 * Only used for backwards compatibility when real data is not provided
 */
const getMockPerson = (personId: string) => {
  return mockPeopleData.find(person => person.id === personId);
};

export const InitialsAvatar: React.FC<InitialsAvatarProps> = ({ 
  personId,
  name,
  avatarUrl: providedAvatarUrl,
  size = 40
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Determine avatar URL and display name
  // Priority: Always use provided props from API. Only use mock data if nothing is provided.
  let avatarUrl = providedAvatarUrl;
  let displayName = name || '';
  
  // Only look up mock data if we have NO real data at all (name is the key indicator)
  // If name is provided, we have real API data, so don't look at mock data
  if (!name && personId) {
    const mockPerson = getMockPerson(personId);
    if (mockPerson) {
      if (!avatarUrl) avatarUrl = mockPerson.avatarUrl;
      displayName = `${mockPerson.firstName} ${mockPerson.lastName}`;
    }
  }
  
  const initials = getInitials(displayName);
  const color = getColorForInitials(initials);
  
  // Reset image state when avatarUrl changes
  React.useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [avatarUrl]);
  
  // Set a timeout to fallback to initials if image doesn't load within 5 seconds
  React.useEffect(() => {
    if (!avatarUrl || imageError || imageLoaded) return;
    
    const timeout = setTimeout(() => {
      if (!imageLoaded) {
        console.warn(`Avatar image failed to load within 5 seconds: ${avatarUrl}`);
        setImageError(true);
      }
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, [avatarUrl, imageError, imageLoaded]);
  
  // If avatar URL exists and no error, try to show image
  if (avatarUrl && !imageError) {
    return (
      <div className="relative flex-shrink-0" style={{ width: `${size}px`, height: `${size}px` }}>
        {/* Skeleton loader - shown while image is loading */}
        {!imageLoaded && (
          <div 
            className="absolute inset-0 rounded-full bg-gray-200 animate-pulse"
            style={{ 
              width: `${size}px`, 
              height: `${size}px`,
            }}
          />
        )}
        
        {/* Actual image - fades in when loaded */}
        <img
          src={avatarUrl}
          alt={displayName}
          className={`rounded-full object-cover transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ 
            width: `${size}px`, 
            height: `${size}px`,
          }}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }
  
  // Fallback to initials
  return (
    <div 
      className="rounded-full flex items-center justify-center font-semibold flex-shrink-0"
      style={{ 
        width: `${size}px`, 
        height: `${size}px`, 
        background: color.bg,
        color: color.text,
        fontSize: `${size * 0.4}px`,
      }}
    >
      {initials}
    </div>
  );
};
